import React, { useEffect, useState } from 'react';
import { FaPlus, FaTrash, FaEdit, FaTimes, FaSave, FaArrowLeft } from 'react-icons/fa';
import { api } from '../../utils/api';
import ImageUploader from '../../components/ImageUploader';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  PageHeader, Panel, FormGroup, FormGrid, SaveBtn, SecondaryBtn, DangerBtn,
  PrimaryBtn, Toast, Row, Table, ItemPill, ImageStrip, ImageThumb,
} from './styles/AdminStyles';

const slugify = (s) => (s || '').toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-');

const emptyCompany = () => ({
  slug: '',
  title: '',
  description: '',
  services: [],
  images: [],
  bannerImage: '',
  logo: '',
  email: '',
  phone: '',
  additionalInfo: '',
  menuLabel: '',
  isPublished: true,
  sortOrder: 0,
});

const ManageCompanies = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // null, 'new', or company object
  const [saving, setSaving] = useState(false);
  const [serviceInput, setServiceInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/companies');
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(emptyCompany());
  const startEdit = async (c) => {
    // fetch full record (includes images)
    try {
      const full = await api.get(`/companies/${c.id}`);
      setEditing({ ...emptyCompany(), ...full });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const setField = (field) => (e) => setEditing({ ...editing, [field]: e.target.value });

  const addService = () => {
    if (!serviceInput.trim()) return;
    setEditing({ ...editing, services: [...(editing.services || []), serviceInput.trim()] });
    setServiceInput('');
  };
  const removeService = (i) => setEditing({ ...editing, services: editing.services.filter((_, idx) => idx !== i) });
  const addImage = (img) => setEditing({ ...editing, images: [...(editing.images || []), img] });
  const removeImage = (i) => setEditing({ ...editing, images: editing.images.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      const payload = {
        slug: editing.slug || slugify(editing.title),
        title: editing.title,
        description: editing.description || '',
        services: editing.services || [],
        images: editing.images || [],
        bannerImage: editing.bannerImage || '',
        logo: editing.logo || '',
        email: editing.email || '',
        phone: editing.phone || '',
        additionalInfo: editing.additionalInfo || '',
        menuLabel: editing.menuLabel || editing.title,
        isPublished: editing.isPublished !== false,
        sortOrder: Number(editing.sortOrder) || 0,
      };
      let savedCompany;
      if (editing.id) {
        const res = await api.put(`/companies/${editing.id}`, payload);
        savedCompany = res?.company || { ...editing, ...payload };
      } else {
        savedCompany = await api.post('/companies', payload);
      }
      api.clearCache();
      setToast({ type: 'success', message: 'Company saved.' });
      setEditing({ ...emptyCompany(), ...savedCompany });
      load();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      api.clearCache();
      setToast({ type: 'success', message: 'Company deleted.' });
      load();
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  if (editing) {
    return (
      <form onSubmit={save}>
        <PageHeader>
          <div className="eyebrow">Companies</div>
          <h1>{editing.id ? 'Edit Company' : 'Create Company'}</h1>
          <p>Each company appears on the public site at <code>/companies/[slug]</code> and in the main menu dropdown.</p>
        </PageHeader>

        <Panel>
          <h2>Basic Info</h2>
          <FormGrid $cols={2}>
            <FormGroup>
              <label>Title *</label>
              <input required value={editing.title || ''} onChange={setField('title')} />
            </FormGroup>
            <FormGroup>
              <label>Slug (URL) *</label>
              <input
                required
                value={editing.slug || ''}
                onChange={setField('slug')}
                onBlur={(e) => { if (!e.target.value && editing.title) setEditing({ ...editing, slug: slugify(editing.title) }); }}
                placeholder="e.g. cera-business-enterprise"
              />
            </FormGroup>
            <FormGroup>
              <label>Menu Label</label>
              <input value={editing.menuLabel || ''} onChange={setField('menuLabel')} />
            </FormGroup>
            <FormGroup>
              <label>Sort Order</label>
              <input type="number" value={editing.sortOrder || 0} onChange={setField('sortOrder')} />
            </FormGroup>
          </FormGrid>
          <FormGroup>
            <label>Description</label>
            <textarea value={editing.description || ''} onChange={setField('description')} />
          </FormGroup>
          <FormGroup>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none', letterSpacing: 'normal' }}>
              <input
                type="checkbox"
                checked={editing.isPublished !== false}
                onChange={e => setEditing({ ...editing, isPublished: e.target.checked })}
                style={{ width: 'auto' }}
              />
              Published (visible on public site)
            </label>
          </FormGroup>
        </Panel>

        <Panel>
          <h2>Banner</h2>
          <ImageUploader
            label="Page Banner"
            value={editing.bannerImage}
            onChange={val => setEditing({ ...editing, bannerImage: val })}
            onRemove={() => setEditing({ ...editing, bannerImage: '' })}
          />
        </Panel>

        <Panel>
          <h2>Logo</h2>
          <div className="subtitle">Shown on the company page and on the company card on the homepage.</div>
          <ImageUploader
            label="Company Logo"
            value={editing.logo}
            onChange={val => setEditing({ ...editing, logo: val })}
            onRemove={() => setEditing({ ...editing, logo: '' })}
          />
        </Panel>

        <Panel>
          <h2>Contact Info</h2>
          <FormGrid $cols={2}>
            <FormGroup>
              <label>Email</label>
              <input type="email" value={editing.email || ''} onChange={setField('email')} placeholder="info@company.com" />
            </FormGroup>
            <FormGroup>
              <label>Phone</label>
              <input value={editing.phone || ''} onChange={setField('phone')} placeholder="+1 555 123 4567" />
            </FormGroup>
          </FormGrid>
        </Panel>

        <Panel>
          <h2>Additional Info</h2>
          <div className="subtitle">Any extra details shown in a dedicated section on the company page.</div>
          <FormGroup>
            <textarea
              rows={5}
              value={editing.additionalInfo || ''}
              onChange={setField('additionalInfo')}
              placeholder="Address, hours, certifications, or any other information"
            />
          </FormGroup>
        </Panel>

        <Panel>
          <h2>Services</h2>
          <div className="subtitle">Services offered by this company.</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              value={serviceInput}
              onChange={e => setServiceInput(e.target.value)}
              placeholder="Add a service"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addService(); } }}
              style={{ padding: '0.75rem 0.9rem', border: '1px solid #E9E9E6', flex: 1 }}
            />
            <SecondaryBtn type="button" onClick={addService}><FaPlus /> Add</SecondaryBtn>
          </div>
          <div>
            {(editing.services || []).map((s, i) => (
              <ItemPill key={i}>
                {s}
                <button type="button" onClick={() => removeService(i)}><FaTimes /></button>
              </ItemPill>
            ))}
          </div>
        </Panel>

        <Panel>
          <h2>Gallery Images</h2>
          <ImageUploader label="Add image" value={null} onChange={addImage} />
          <ImageStrip>
            {(editing.images || []).map((img, i) => (
              <ImageThumb key={i}>
                <img src={img} alt="" />
                <button type="button" onClick={() => removeImage(i)}><FaTimes /></button>
              </ImageThumb>
            ))}
          </ImageStrip>
        </Panel>

        <Row>
          <SaveBtn type="submit" disabled={saving}><FaSave /> {saving ? 'Saving...' : 'Save Company'}</SaveBtn>
          <SecondaryBtn type="button" onClick={() => setEditing(null)}><FaArrowLeft /> Back</SecondaryBtn>
          {toast && <Toast $error={toast.type === 'error'}>{toast.message}</Toast>}
        </Row>
      </form>
    );
  }

  return (
    <>
      <PageHeader>
        <div className="eyebrow">Content</div>
        <h1>Manage Companies</h1>
        <p>Create and maintain the pages that show up in the Companies dropdown.</p>
      </PageHeader>

      <Row style={{ marginBottom: '1rem' }}>
        <PrimaryBtn onClick={startNew}><FaPlus /> New Company</PrimaryBtn>
        {toast && <Toast $error={toast.type === 'error'}>{toast.message}</Toast>}
      </Row>

      <Panel>
        <Table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Slug</th>
              <th>Published</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>No companies yet.</td></tr>
            )}
            {list.map(c => (
              <tr key={c.id}>
                <td>{c.sortOrder || 0}</td>
                <td>{c.title}</td>
                <td><code>{c.slug}</code></td>
                <td>{c.isPublished !== false ? 'Yes' : 'No'}</td>
                <td>
                  <Row>
                    <SecondaryBtn type="button" onClick={() => startEdit(c)}><FaEdit /> Edit</SecondaryBtn>
                    <DangerBtn type="button" onClick={() => remove(c)}><FaTrash /></DangerBtn>
                  </Row>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </>
  );
};

export default ManageCompanies;
